'use client'

import { useState } from 'react'
import { CircleCheck } from 'lucide-react'
import Image from 'next/image'

import { Button } from '@/components/elements/Button'
import { formatPrice, formatDateNormal } from '@/utils/util'
import { calcDepositBreakdown, DEPOSIT_LABELS, DEPOSIT_MIN_LEG } from '@/utils/order'
import { useBalancePayment } from '@/lib/commerce/hooks/useBalancePayment'
import type { PaymentEndpoint } from '@/lib/commerce/services/payment-api'

import Qpay from '@/assets/qpay.svg'
import PocketIcon from '@/assets/pocketzero.svg'
import OmniwayIcon from '@/assets/omniway.png'

/*
  Split payment: the state a pre-order sits in for its whole lead time — deposit banked,
  balance outstanding, goods not yet handed over.

  Shared across all three templates because the arithmetic and the Mongolian wording are
  the parts that must not drift; the templates keep their own chrome elsewhere.

  Choosing a rail here does NOT open a QR screen. It mints the invoice, refreshes the
  route, and the page falls through to the ordinary rail block — which already renders
  getPayableAmount, i.e. the balance. See useBalancePayment.
*/
export default function DepositBalanceCard({ order, vendor, onRefresh }: { order: any, vendor: any, onRefresh?: () => void }) {
  const { depositAmount, depositPaidAt, outstanding } = calcDepositBreakdown(order)
  const { loading, pay } = useBalancePayment({ order, vendor, onRefresh })
  const [pending, setPending] = useState<PaymentEndpoint | null>(null)

  const submit = async (method: PaymentEndpoint) => {
    setPending(method)
    await pay(method)
    setPending(null)
  }

  /*
    BNPL is absent by design. Those rails underwrite a purchase; financing the tail of one
    the customer has already part-paid, ten days after the fact, is not a product anyone
    asked for — and their 100,000₮ floor would reject most balances anyway. The backend
    refuses them for this leg too.
  */
  const rails: { id: PaymentEndpoint, name: string, desc: string, icon: any }[] = []
  if (vendor?.qpayMerchant && vendor?.canQpay !== false) {
    rails.push({ id: 'qpay', name: 'QPay', desc: 'QR код уншуулж төлөх', icon: Qpay })
  }
  if (vendor?.pocketCredentials && vendor?.canPocket !== false && outstanding >= 100000) {
    rails.push({ id: 'pocket', name: 'PocketZero', desc: 'QR код уншуулж төлөх', icon: PocketIcon })
  }
  if (vendor?.omniwayCredentials && vendor?.canOmniway !== false && outstanding >= 100000) {
    rails.push({ id: 'omniway', name: 'OmniWay', desc: 'QR код уншуулж төлөх', icon: OmniwayIcon })
  }

  // Only reachable through a partial refund. No rail will invoice under the floor, so
  // there is nothing honest to offer — the vendor closes it out on their side.
  const belowMin = outstanding > 0 && outstanding < DEPOSIT_MIN_LEG

  return (
    <div className="rounded-2xl bg-white border border-border p-5 flex flex-col gap-4">
      {/* Confirmation first. The deposit is what confirms a pre-order, so the headline is
          the good news — not a demand for the balance, which is not due until handover. */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-green-50 text-green-700 grid place-items-center shrink-0">
          <CircleCheck className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-foreground">Захиалга баталгаажлаа</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {DEPOSIT_LABELS.deposit} <strong>{formatPrice(depositAmount) || '0₮'}</strong> төлөгдсөн
            {depositPaidAt ? ` · ${formatDateNormal(depositPaidAt)}` : ''}
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-muted/40 border border-border px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs font-medium text-muted-foreground">{DEPOSIT_LABELS.balance}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Барааг хүлээн авахдаа төлнө</div>
        </div>
        <span className="text-2xl font-semibold tabular-nums">{formatPrice(outstanding) || '0₮'}</span>
      </div>

      {outstanding <= 0 ? (
        <p className="text-xs text-muted-foreground">{DEPOSIT_LABELS.noBalance}</p>
      ) : belowMin ? (
        <p className="text-xs text-muted-foreground">
          Үлдэгдэл {formatPrice(outstanding)} нь төлбөрийн доод дүнгээс бага тул онлайнаар төлөх боломжгүй. Барааг хүлээн авахдаа төлнө үү.
        </p>
      ) : rails.length > 0 ? (
        /*
          Paying online is OPTIONAL, not hidden. Outline styling rather than a full-width
          primary button keeps it from reading as "you must pay this now" — but it stays
          visible, because a customer who wants to settle early should not have to go
          looking for the way to do it.
        */
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">Хүсвэл үлдэгдлээ одоо төлөх боломжтой</p>
          {rails.map(rail => (
            <Button
              key={rail.id}
              onClick={() => submit(rail.id)}
              disabled={loading}
              variant="outline"
              border={vendor?.border}
              color={vendor?.color?.primary}
              className="w-full h-11 flex items-center justify-center gap-2"
            >
              <Image src={rail.icon} alt={rail.name} width={18} height={18} className="rounded" />
              {pending === rail.id ? 'Нэхэмжлэл үүсгэж байна...' : `${DEPOSIT_LABELS.payBalance} · ${rail.name}`}
            </Button>
          ))}
        </div>
      ) : null}

      {order?.depositTerms && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="text-xs font-semibold text-amber-900">{DEPOSIT_LABELS.terms}</div>
          {/* Vendor-authored plain text — rendered as text, never as HTML. */}
          <p className="text-xs text-amber-800 mt-1 whitespace-pre-line">{order.depositTerms}</p>
        </div>
      )}
    </div>
  )
}
